import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { getSocket } from 'common/socket';
import { getOrderListDB, updateOrderListDB } from 'slice/orderSliceDB';
const ORDER_STATUSES = ['pending', 'dispatch', 'complete', 'reject'];
const ORDER_PLANS = ['basic', 'standard', 'premium'];
const STATUS_BADGE_CLASS_MAP = {
  pending: 'bg-warning',
  dispatch: 'bg-info',
  complete: 'bg-success',
  reject: 'bg-danger'
};
const MODAL_CONTAINER_STYLE = {
  display: 'block',
  padding: '0.75rem',
  overflowY: 'auto'
};
const MODAL_DIALOG_STYLE = {
  width: 'calc(100vw - 1.5rem)',
  maxWidth: '720px',
  margin: '1rem auto'
};
const MODAL_CONTENT_STYLE = {
  maxHeight: 'calc(100vh - 2rem)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};
const MODAL_BODY_STYLE = {
  overflowY: 'auto',
  paddingBottom: '1rem'
};

const normalizeOrders = (orders) => {
  if (Array.isArray(orders)) {
    return orders;
  }

  if (Array.isArray(orders?.data)) {
    return orders.data;
  }

  return [];
};

const formatLabel = (value) => {
  if (!value) {
    return 'N/A';
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : `Rs ${numericValue.toFixed(2)}`;
};

const getOrderPhone = (order) => order?.phone || order?.customer?.mobile || 'N/A';
const getOrderAddress = (order) => order?.shipping_address || order?.customer?.address || '';
const getCustomerName = (order) => order?.customer?.name || 'N/A';

const getErrorMessage = (error) => {
  if (!error) {
    return '';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  if (typeof error?.data?.message === 'string') {
    return error.data.message;
  }

  if (typeof error?.response?.data?.message === 'string') {
    return error.response.data.message;
  }

  return 'Unable to load orders right now.';
};

const buildEditableOrder = (order) => ({
  ...order,
  plan: order?.plan || ORDER_PLANS[0],
  payment_method: order?.payment_method || '',
  shipping_address: getOrderAddress(order),
  phone: order?.phone || order?.customer?.mobile || '',
  status: order?.status || ORDER_STATUSES[0],
  total_amount: order?.total_amount ?? ''
});

const CorderList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, loading, error } = useSelector((state) => state.orderDB);
  const [showModal, setShowModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const orderList = useMemo(() => normalizeOrders(data), [data]);
  const orderErrorMessage = useMemo(() => getErrorMessage(error), [error]);

  useEffect(() => {
    dispatch(getOrderListDB());
  }, [dispatch]);

  useEffect(() => {
    let socket;

    try {
      socket = getSocket();
    } catch (socketError) {
      console.warn('Socket connection unavailable for order updates.', socketError);
      return undefined;
    }

    const handleRealtimeOrder = () => {
      dispatch(getOrderListDB());
    };

    socket.on('new-payment', handleRealtimeOrder);

    return () => {
      socket.off('new-payment', handleRealtimeOrder);
    };
  }, [dispatch]);

  const handleEditClick = (order) => {
    setCurrentOrder(buildEditableOrder(order));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentOrder(null);
  };

  const handleFieldChange = (field) => (event) => {
    const { value } = event.target;
    setCurrentOrder((previousOrder) => {
      if (!previousOrder) {
        return previousOrder;
      }

      return {
        ...previousOrder,
        [field]: value
      };
    });
  };

  const handleSave = () => {
    if (!currentOrder) {
      return;
    }

    const payload = {
      status: currentOrder.status,
      plan: currentOrder.plan,
      total_amount: currentOrder.total_amount,
      shipping_address: currentOrder.shipping_address,
      phone: currentOrder.phone,
      payment_method: currentOrder.payment_method
    };

    dispatch(updateOrderListDB({ id: currentOrder.id, data: payload }));
    handleCloseModal();
  };

  const tableHeaders = [
    {
      Header: 'Order ID',
      accessor: 'order_id',
      Cell: ({ row }) => row.original.order_id || `#${row.original.id}`
    },
    {
      Header: 'Customer',
      accessor: 'customer.name',
      Cell: ({ row }) => getCustomerName(row.original)
    },
    {
      Header: 'Plan',
      accessor: 'plan',
      Cell: ({ row }) => formatLabel(row.original.plan)
    },
    {
      Header: 'Amount',
      accessor: 'total_amount',
      Cell: ({ row }) => formatAmount(row.original.total_amount)
    },
    {
      Header: 'Phone',
      accessor: 'phone',
      Cell: ({ row }) => getOrderPhone(row.original)
    },
    {
      Header: 'Payment',
      accessor: 'payment_method',
      Cell: ({ row }) => row.original.payment_method || 'N/A'
    },
    {
      Header: 'Date',
      accessor: 'createdAt'
    },
    {
      Header: 'Address',
      accessor: 'shipping_address',
      Cell: ({ row }) => <TruncatedMessage text={getOrderAddress(row.original)} maxLength={30} />
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => {
        const status = row.original.status || '';
        const badgeClass = STATUS_BADGE_CLASS_MAP[status] || 'bg-secondary';

        return <span className={`badge ${badgeClass} text-white p-2`}>{formatLabel(status)}</span>;
      }
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="order-action-group">
          <button type="button" className="btn editbtn order-action-btn" onClick={() => handleEditClick(row.original)}>
            Update
          </button>
          <button type="button" className="btn editbtn order-action-btn" onClick={() => navigate(`/billing/details/${row.original.id}`)}>
            View Bill
          </button>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Order List</h4>
        </div>

        {loading && !orderList.length ? <div className="alert alert-info mt-4 mx-4">Loading orders...</div> : null}
        {!loading && orderErrorMessage ? <div className="alert alert-danger mt-4 mx-4">{orderErrorMessage}</div> : null}
        {!loading && !orderErrorMessage && !orderList.length ? <div className="alert alert-secondary mt-4 mx-4">No orders found.</div> : null}
        {orderList.length > 0 ? <AdminTable tableHeaders={tableHeaders} tableData={orderList} /> : null}
      </div>

      {showModal && currentOrder ? (
        <>
          <div
            className="modal fade show"
            style={MODAL_CONTAINER_STYLE}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="editModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document" style={MODAL_DIALOG_STYLE}>
              <div className="modal-content" style={MODAL_CONTENT_STYLE}>
                <div className="modal-header">
                  <h5 className="modal-title" id="editModalLabel">
                    Update Order
                  </h5>
                  <button type="button" className="close" onClick={handleCloseModal} aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body" style={MODAL_BODY_STYLE}>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleSave();
                    }}
                  >
                    <div className="form-group">
                      <label htmlFor="orderId">Order ID</label>
                      <input type="text" className="form-control" id="orderId" value={currentOrder.order_id || ''} readOnly />
                    </div>
                    <div className="form-group">
                      <label htmlFor="customerName">Customer</label>
                      <input type="text" className="form-control" id="customerName" value={getCustomerName(currentOrder)} readOnly />
                    </div>
                    <div className="form-group">
                      <label htmlFor="orderStatus">Status</label>
                      <select className="form-control" id="orderStatus" value={currentOrder.status} onChange={handleFieldChange('status')}>
                        {ORDER_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {formatLabel(status)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="orderPlan">Plan</label>
                      <select className="form-control" id="orderPlan" value={currentOrder.plan} onChange={handleFieldChange('plan')}>
                        {ORDER_PLANS.map((plan) => (
                          <option key={plan} value={plan}>
                            {formatLabel(plan)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="paymentMethod">Payment Method</label>
                      <input
                        type="text"
                        className="form-control"
                        id="paymentMethod"
                        value={currentOrder.payment_method}
                        onChange={handleFieldChange('payment_method')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="orderAmount">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id="orderAmount"
                        value={currentOrder.total_amount}
                        onChange={handleFieldChange('total_amount')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="orderPhone">Contact Number</label>
                      <input
                        type="text"
                        className="form-control"
                        id="orderPhone"
                        value={currentOrder.phone}
                        onChange={handleFieldChange('phone')}
                      />
                    </div>
                    <div className="form-group mb-0">
                      <label htmlFor="orderAddress">Shipping Address</label>
                      <textarea
                        className="form-control"
                        id="orderAddress"
                        value={currentOrder.shipping_address}
                        onChange={handleFieldChange('shipping_address')}
                        rows={3}
                      />
                    </div>
                  </form>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Close
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      ) : null}
    </>
  );
};

export default CorderList;
